import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { motion, useMotionValue, useSpring } from 'motion/react'

type CursorVariant = 'default' | 'link' | 'text'

const INTERACTIVE = 'a, button, [role="button"], label, select, summary, [data-cursor="link"]'
const TEXT_ENTRY = 'input:not([type="radio"]):not([type="checkbox"]), textarea, [data-cursor="text"]'

const variants = {
    default: { width: 34, height: 34, opacity: 1 },
    link: { width: 64, height: 64, opacity: 1 },
    text: { width: 4, height: 28, opacity: 1 }
}

const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const [visible, setVisible] = useState(false)
    const [variant, setVariant] = useState<CursorVariant>('default')

    const x = useMotionValue(-100)
    const y = useMotionValue(-100)
    const springX = useSpring(x, { stiffness: 520, damping: 42, mass: 0.5 })
    const springY = useSpring(y, { stiffness: 520, damping: 42, mass: 0.5 })

    useEffect(() => {
        const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
        const update = () => setEnabled(finePointer.matches && !reduce.matches)

        update()
        finePointer.addEventListener('change', update)
        reduce.addEventListener('change', update)

        return () => {
            finePointer.removeEventListener('change', update)
            reduce.removeEventListener('change', update)
        }
    }, [])

    useEffect(() => {
        if (!enabled) return

        const handleMove = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return

            x.set(event.clientX)
            y.set(event.clientY)
            setVisible(true)
        }

        const handleOver = (event: PointerEvent) => {
            const target = event.target instanceof Element ? event.target : null

            if (target?.closest(TEXT_ENTRY)) setVariant('text')
            else if (target?.closest(INTERACTIVE)) setVariant('link')
            else setVariant('default')
        }

        const handleLeave = () => setVisible(false)

        window.addEventListener('pointermove', handleMove, { passive: true })
        document.addEventListener('pointerover', handleOver, { passive: true })
        document.documentElement.addEventListener('pointerleave', handleLeave)

        return () => {
            window.removeEventListener('pointermove', handleMove)
            document.removeEventListener('pointerover', handleOver)
            document.documentElement.removeEventListener('pointerleave', handleLeave)
        }
    }, [enabled, x, y])

    if (!enabled) return null

    return (
        <motion.div
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[100] mix-blend-difference'
            style={{ x: springX, y: springY }}
        >
            <motion.div
                className='-translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white'
                initial={false}
                animate={visible ? variants[variant] : { ...variants[variant], opacity: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            />
        </motion.div>
    )
}

export default Cursor
